import { ChangeDetectionStrategy, Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, ChangeDetectorRef, WritableSignal, computed, signal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { createSearchPattern } from '../../utils';

@Component({
  selector: 'insights-content-list',
  templateUrl: './insights-content-list.component.html',
  styleUrls: ['./insights-content-list.component.scss'],
  standalone: false
})
export class InsightsContentListComponent implements OnChanges {
  @Input() readonly = false;
  @Input() disabled = false;

  @Input() categories: InsightsContentListCategory[] = [];

  @Input() filter = '';
  @Output() filterChange = new EventEmitter<string>();

  @Input() selectedItem: InsightsContentListItem = null;
  @Output() selectedItemChange = new EventEmitter<InsightsContentListItem>();

  @Output() selectionChange = new EventEmitter<InsightsContentSelectionChangeEvent>();

  filterCtrl = new FormControl('');
  filterPattern: RegExp = null;

  private setFilter(filter: string, emitEvent: boolean) {
    filter = filter?.trim() ?? '';

    if (filter != '') {
      let pattern = createSearchPattern(filter);

      this.filterPattern = pattern;

      // Apply filter to all items and update parent items
      // For any items where filter is a match, ensure they aren't collapsed
      this.categoryStates.forEach(c => {
        let anyGroupMatch = false;

        c.groupStates.forEach(g => {
          let anyItemMatch = false;

          g.itemStates.forEach(i => {
            i.titleFilterMatch.set(pattern.test(i.item.title));
            if (i.titleFilterMatch)
              anyItemMatch = true;
          });

          if (anyItemMatch) {
            g.collapsed.set(false);
            anyGroupMatch = true;
          }
        });

        if (anyGroupMatch)
          c.collapsed.set(false);
      });
    } else {
      this.filterPattern = null;

      this.categoryStates.forEach(c => {
        c.groupStates.forEach(g => {
          g.itemStates.forEach(i => {
            i.titleFilterMatch.set(true);
          });
        });
      });
    }

    if (emitEvent)
      this.filterChange.emit(filter);
  }

  categoryStates: InsightsContentListCategoryState[] = [];

  anyCategoryVisible = computed(() => this.categoryStates.some(x => !x.hidden()));

  private itemToState = new Map<number, InsightsContentListItemState>();
  private selectedItemState: InsightsContentListItemState = null;

  private createCategoryStates() {
    this.categoryStates = this.categories?.map(c => ({
      id: c.id,
      category: c,
      collapsed: signal(false),
      visibleGroupCount: signal(0),
      visibleItemCount: signal(0),
      hidden: signal(false),
      groupStates: c.groups?.map(g => ({
        id: g.id,
        group: g,
        collapsed: signal(false),
        visibleItemCount: signal(0),
        hidden: signal(true),
        itemStates: g.items.map(i => ({
          id: i.id,
          item: i,
          selected: signal(false),
          titleFilterMatch: signal(true),
          hidden: signal(false)
        }))
      }))
    }));
  }

  private createItemToStateMap() {
    this.itemToState.clear();

    this.categoryStates.forEach(c => {
      c.groupStates.forEach(g => {
        g.itemStates.forEach(i => this.itemToState.set(i.item.id, i));
      })
    });
  }

  private updateVisibility() {
    this.categoryStates.forEach(c => {
      c.groupStates.forEach(g => {
        g.itemStates.forEach(i => {
          i.hidden.set(!i.titleFilterMatch() && !i.selected());
          return i;
        });

        g.visibleItemCount.set(g.itemStates.filter(i => !i.hidden()).length),
        g.hidden.set(!g.visibleItemCount());
        return g;
      });

      c.visibleGroupCount.set(c.groupStates.filter(g => !g.hidden()).length);
      c.visibleItemCount.set(c.groupStates.reduce((i, g) => i + g.visibleItemCount(), 0));
      c.hidden.set(!c.visibleGroupCount);
     
      return c;
    });
  }

  setSelectedItem(
    categoryState: InsightsContentListCategoryState,
    groupState: InsightsContentListGroupState,
    itemState: InsightsContentListItemState) {
    if (this.disabled || this.readonly)
      return;
     
    this.selectedItemState?.selected.set(false);
    this.selectedItemState = itemState;
    this.selectedItemState?.selected.set(true);

    this.selectedItem = itemState?.item;
    this.onSelectedItemChange(categoryState, groupState, itemState);
    this.updateVisibility();
  }

  private onSelectedItemChange(
    categoryState: InsightsContentListCategoryState,
    groupState: InsightsContentListGroupState,
    itemState: InsightsContentListItemState) {
    if (itemState) {
      this.selectionChange.emit({
        category: categoryState.category,
        group: groupState.group,
        item: itemState.item
      });

      this.selectedItemChange.emit(itemState.item);
    }
    else {
      this.selectionChange.emit(null);
      this.selectedItemChange.emit(null);
    }
  }

  private updateSelectedItemOnChange() {
    let selectedItemState = this.selectedItem ? this.itemToState.get(this.selectedItem.id) : null;

    this.selectedItemState?.selected.set(false);
    this.selectedItemState = selectedItemState;
    this.selectedItemState?.selected.set(true);
  }

  toggleCollapsed(item: { collapsed: WritableSignal<boolean> }) {
    if (this.disabled)
      return;

    item.collapsed.set(!item.collapsed());
  }


  constructor(private ref: ChangeDetectorRef) {
    this.filterCtrl.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged()
    ).subscribe(s => {
      this.setFilter(s, true);
      this.updateVisibility();
      this.ref.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    let updateVisibility = false;

    if (changes.categories) {
      this.createCategoryStates();
      this.createItemToStateMap();
      updateVisibility = true;
    }

    if (changes.selectedItem) {
      this.updateSelectedItemOnChange();
      updateVisibility = true;
    }

    if (changes.filter) {
      this.setFilter(this.filter, false);
      this.filterCtrl.setValue(this.filter, { emitEvent: false });
      updateVisibility = true;
    }
    
    if (updateVisibility) {
      this.updateVisibility();
    }

    if (changes.disabled || changes.readonly) {
      if (this.disabled || this.readonly)
        this.filterCtrl.disable();
      else
        this.filterCtrl.enable();
    }
  }


  public toggleCollapseAll(collapsed: boolean) {
    this.categoryStates.forEach(c => {
      c.collapsed.set(collapsed);
      c.groupStates.forEach(g => {
        g.collapsed.set(collapsed);
      });
    });
  }
}

export interface InsightsContentListCategory<T = unknown> {
  id: number;
  iconClass?: string;
  iconUrl?: string;
  title: string;
  groups: InsightsContentListItemGroup<T>[];
}

export interface InsightsContentListItemGroup<T = unknown> {
  id: number;
  title: string;
  items: InsightsContentListItem<T>[];
}

export interface InsightsContentListItem<T = unknown> {
  id: number;
  title: string;
  description?: string;
  data?: T;
}

export interface InsightsContentListCategoryState {
  id: number;
  category: InsightsContentListCategory;
  collapsed: WritableSignal<boolean>;
  visibleGroupCount: WritableSignal<number>;
  visibleItemCount: WritableSignal<number>;
  hidden: WritableSignal<boolean>;
  groupStates: InsightsContentListGroupState[];
}

export interface InsightsContentListGroupState {
  id: number;
  group: InsightsContentListItemGroup;
  collapsed: WritableSignal<boolean>;
  visibleItemCount: WritableSignal<number>;
  hidden: WritableSignal<boolean>;
  itemStates: InsightsContentListItemState[];
}

export interface InsightsContentListItemState {
  id: number;
  item: InsightsContentListItem;
  selected: WritableSignal<boolean>;
  titleFilterMatch: WritableSignal<boolean>;
  hidden: WritableSignal<boolean>;
}

export interface InsightsContentSelectionChangeEvent {
  category: InsightsContentListCategory;
  group: InsightsContentListItemGroup;
  item: InsightsContentListItem;
}
